/**
 * ResolveControlBar — the pause / stop / resume strip for a bulk "Fix with
 * Asthra" pass. Rendered above the spine while a pass is in flight or paused,
 * and — once a pass has queued changes — a "Review N changes" strip that takes
 * the writer to the Issues tab, where the review cards live.
 *
 * Separate from ``SpineControlBar`` (which controls the DRAFTING run) because a
 * resolve pass never enters the reducer's mid-run state — it is its own thing,
 * with its own lifecycle owned by ``useResolveIssuesFlow``. Pure presentation.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import type { ResolvePhase, ResolveProgress } from "../hooks/useResolveIssuesFlow";

interface ResolveControlBarProps {
  phase: ResolvePhase;
  progress: ResolveProgress;
  remainingCount: number;
  onPause: () => void;
  onStop: () => void;
  onResume: () => void;
  onDiscard: () => void;
  /** Why the last start / resume was refused — shown until dismissed. */
  error?: string | null;
  onDismissError?: () => void;
  /** Changes the last pass queued for review. With ``onReview``, an idle bar
   *  shows a "Review N changes" strip. */
  reviewCount?: number;
  /** Switch to the Issues tab (the review cards). */
  onReview?: () => void;
  /** Hide the review strip. */
  onDismissReview?: () => void;
  /** Open the Fix window again on the pass's per-issue progress. */
  onShowProgress?: () => void;
}

// Film register: 13px semibold headline, mono progress meta, a thin
// brand progress track, and compact outlined pill buttons.
const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    gap: "10px",
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    padding: "9px 12px 10px",
    fontFamily: fonts.body,
  },
  dot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: brandColors.primary,
    flexShrink: 0,
    animationName: {
      "0%": { opacity: 0.35 },
      "50%": { opacity: 1 },
      "100%": { opacity: 0.35 },
    },
    animationDuration: "1600ms",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": { animationName: "none" },
  },
  dotPaused: { background: statusColors.warn, animationName: "none" },
  dotError: { background: statusColors.danger, animationName: "none" },
  dotReady: { background: statusColors.cool, animationName: "none" },
  errorText: {
    flex: 1,
    fontSize: "12px",
    lineHeight: "16px",
    color: inkColors.ink,
  },
  label: {
    fontSize: "13px",
    fontWeight: 600,
    color: inkColors.ink,
    letterSpacing: "-0.1px",
  },
  count: {
    fontSize: "10px",
    fontWeight: 600,
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
  },
  spacer: { flex: 1 },
  // Progress track along the bottom edge (running only).
  track: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "2px",
    background: surfaceColors.line,
  },
  trackFill: {
    height: "100%",
    background: brandColors.primary,
    transition: "width 300ms ease-out",
  },
  button: {
    background: "var(--surface-raised)",
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.6px",
    padding: "4px 9px",
    textTransform: "uppercase",
    transition: "all 150ms",
    "&:hover": { color: brandColors.primary, border: `1px solid ${brandColors.primary}` },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
  stopButton: { "&:hover": { color: statusColors.danger, border: `1px solid ${statusColors.danger}` } },
  resumeButton: {
    background: brandColors.primary,
    border: `1px solid ${brandColors.primary}`,
    color: "var(--on-brand)",
    "&:hover": { color: "var(--on-brand)", background: brandColors.mid, border: `1px solid ${brandColors.mid}` },
  },
});

const ResolveControlBar: React.FC<ResolveControlBarProps> = ({
  phase,
  progress,
  remainingCount,
  onPause,
  onStop,
  onResume,
  onDiscard,
  error,
  onDismissError,
  reviewCount = 0,
  onReview,
  onDismissReview,
  onShowProgress,
}) => {
  const styles = useStyles();
  if (phase === "idle" && error) {
    return (
      <div className={styles.root} role="alert">
        <span className={mergeClasses(styles.dot, styles.dotError)} aria-hidden />
        <span className={styles.errorText}>{error}</span>
        {onDismissError ? (
          <button type="button" className={styles.button} onClick={onDismissError}>
            Dismiss
          </button>
        ) : null}
      </div>
    );
  }
  if (phase === "idle") {
    if (reviewCount <= 0 || !onReview) return null;
    return (
      <div className={styles.root} aria-live="polite" data-testid="resolve-review-strip">
        <span className={mergeClasses(styles.dot, styles.dotReady)} aria-hidden />
        <span className={styles.label}>
          {reviewCount} change{reviewCount === 1 ? "" : "s"} ready to review
        </span>
        <span className={styles.spacer} />
        {onShowProgress ? (
          <button type="button" className={styles.button} onClick={onShowProgress}>
            See results
          </button>
        ) : null}
        <button type="button" className={mergeClasses(styles.button, styles.resumeButton)} onClick={onReview}>
          Review {reviewCount} change{reviewCount === 1 ? "" : "s"}
        </button>
        {onDismissReview ? (
          <button type="button" className={styles.button} onClick={onDismissReview} aria-label="Hide">
            Later
          </button>
        ) : null}
      </div>
    );
  }

  const isPaused = phase === "paused";
  const busy = phase === "pausing" || phase === "stopping";
  const flags = progress.flags;
  const headline = isPaused
    ? "Fix paused"
    : phase === "pausing"
      ? "Pausing…"
      : phase === "stopping"
        ? "Stopping…"
        : flags > 0
          ? `Review agent is fixing ${flags} flag${flags === 1 ? "" : "s"}`
          : "Review agent is fixing flags";

  return (
    <div className={styles.root} role="status" aria-live="polite">
      <span className={mergeClasses(styles.dot, isPaused && styles.dotPaused)} aria-hidden />
      <span className={styles.label}>{headline}</span>
      {error ? (
        // Any phase: a failed Pause/Stop request must not leave the bar silent.
        <span className={styles.errorText} role="alert">
          {error}
        </span>
      ) : isPaused ? (
        <span className={styles.count}>
          {remainingCount} section{remainingCount === 1 ? "" : "s"} left
        </span>
      ) : (
        <span className={styles.count}>
          · {progress.done} of {progress.total} section{progress.total === 1 ? "" : "s"}
        </span>
      )}
      <span className={styles.spacer} />
      {!isPaused && progress.total > 0 ? (
        <span className={styles.track} aria-hidden="true">
          <span
            className={styles.trackFill}
            style={{ display: "block", width: `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` }}
          />
        </span>
      ) : null}
      {onShowProgress ? (
        <button type="button" className={styles.button} onClick={onShowProgress}>
          Show progress
        </button>
      ) : null}
      {isPaused ? (
        <>
          <button type="button" className={mergeClasses(styles.button, styles.resumeButton)} onClick={onResume}>
            Resume
          </button>
          <button type="button" className={styles.button} onClick={onDiscard}>
            Discard
          </button>
        </>
      ) : (
        <>
          <button type="button" className={styles.button} onClick={onPause} disabled={busy}>
            Pause
          </button>
          <button
            type="button"
            className={mergeClasses(styles.button, styles.stopButton)}
            onClick={onStop}
            disabled={busy}
          >
            Stop
          </button>
        </>
      )}
    </div>
  );
};

export default ResolveControlBar;
